<template>
    <DashboardLayout title="Create SLA Policy">
        <div class="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div class="bg-white shadow rounded-lg">
                <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 class="text-xl font-semibold text-gray-900">Create New SLA Policy</h2>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Define service level agreements for work orders based on specific criteria.</p>
                </div>

                <form @submit.prevent="submit" class="px-6 py-6 space-y-6">
                    <!-- Name Field -->
                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Policy Name <span class="text-red-500">*</span>
                        </label>
                        <input
                            id="name"
                            v-model="form.name"
                            type="text"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.name }"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600">
                            {{ form.errors.name }}
                        </p>
                    </div>

                    <!-- Description Field -->
                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.description }"
                        ></textarea>
                        <p v-if="form.errors.description" class="mt-1 text-sm text-red-600">
                            {{ form.errors.description }}
                        </p>
                    </div>

                    <!-- Criteria Section -->
                    <div class="bg-gray-50 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Matching Criteria</h3>
                        <p class="text-sm text-gray-600 mb-4">Leave criteria blank to apply to all work orders of that type. More specific policies will take precedence.</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Work Order Type -->
                            <div>
                                <label for="work_order_type" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Work Order Type
                                </label>
                                <select
                                    id="work_order_type"
                                    v-model="form.work_order_type"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.work_order_type }"
                                >
                                    <option value="">All Types</option>
                                    <option value="corrective">Corrective</option>
                                    <option value="preventive">Preventive</option>
                                    <option value="predictive">Predictive</option>
                                    <option value="inspection">Inspection</option>
                                    <option value="calibration">Calibration</option>
                                    <option value="emergency">Emergency</option>
                                </select>
                                <p v-if="form.errors.work_order_type" class="mt-1 text-sm text-red-600">
                                    {{ form.errors.work_order_type }}
                                </p>
                            </div>

                            <!-- Priority -->
                            <div>
                                <label for="priority" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Priority
                                </label>
                                <select
                                    id="priority"
                                    v-model="form.priority"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.priority }"
                                >
                                    <option value="">All Priorities</option>
                                    <option value="low">Low</option>
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                                <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600">
                                    {{ form.errors.priority }}
                                </p>
                            </div>

                            <!-- Service Category -->
                            <div>
                                <label for="service_category_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Service Category
                                </label>
                                <select
                                    id="service_category_id"
                                    v-model="form.service_category_id"
                                    @change="onCategoryChange"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.service_category_id }"
                                >
                                    <option value="">All Categories</option>
                                    <option v-for="category in serviceCategories" :key="category.id" :value="category.id">
                                        {{ category.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.service_category_id" class="mt-1 text-sm text-red-600">
                                    {{ form.errors.service_category_id }}
                                </p>
                            </div>

                            <!-- Service -->
                            <div>
                                <label for="service_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Service
                                </label>
                                <select
                                    id="service_id"
                                    v-model="form.service_id"
                                    :disabled="!form.service_category_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    :class="{ 'border-red-300': form.errors.service_id }"
                                >
                                    <option value="">All Services</option>
                                    <option v-for="service in availableServices" :key="service.id" :value="service.id">
                                        {{ service.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.service_id" class="mt-1 text-sm text-red-600">
                                    {{ form.errors.service_id }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- SLA Times Section -->
                    <div class="bg-gray-50 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 mb-4">SLA Time Targets</h3>
                        <p class="text-sm text-gray-600 mb-4">Define time limits in hours for response and resolution.</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Response Time -->
                            <div>
                                <label for="response_time_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Response Time (hours)
                                </label>
                                <input
                                    id="response_time_hours"
                                    v-model="form.response_time_hours"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="e.g., 4.00"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.response_time_hours }"
                                />
                                <p class="mt-1 text-sm text-gray-500">Time until work order must be acknowledged/started</p>
                                <p v-if="form.errors.response_time_hours" class="mt-1 text-sm text-red-600">
                                    {{ form.errors.response_time_hours }}
                                </p>
                            </div>

                            <!-- Resolution Time -->
                            <div>
                                <label for="resolution_time_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Resolution Time (hours)
                                </label>
                                <input
                                    id="resolution_time_hours"
                                    v-model="form.resolution_time_hours"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="e.g., 24.00"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.resolution_time_hours }"
                                />
                                <p class="mt-1 text-sm text-gray-500">Time until work order must be completed</p>
                                <p v-if="form.errors.resolution_time_hours" class="mt-1 text-sm text-red-600">
                                    {{ form.errors.resolution_time_hours }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Is Active Checkbox -->
                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="is_active"
                                v-model="form.is_active"
                                type="checkbox"
                                class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="is_active" class="font-medium text-gray-700">Active</label>
                            <p class="text-gray-500">Make this policy available for matching immediately.</p>
                        </div>
                    </div>

                    <!-- Info Box -->
                    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="ml-3 flex-1">
                                <p class="text-sm text-blue-700">
                                    <strong>Specificity Matching:</strong> Policies with more specific criteria (service > category > type/priority) will be matched first. For example, a policy for "Electrical > Power Outage > Urgent" will be used instead of a general "Urgent" policy.
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Link
                            :href="route('settings.sla-policies.index')"
                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            :disabled="form.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span v-if="form.processing">Creating...</span>
                            <span v-else>Create SLA Policy</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { computed } from 'vue';
import { useForm, Link } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
    serviceCategories: Array,
});

const toast = useToast();

const form = useForm({
    name: '',
    description: '',
    work_order_type: '',
    service_category_id: '',
    service_id: '',
    priority: '',
    response_time_hours: '',
    resolution_time_hours: '',
    is_active: true,
});

const availableServices = computed(() => {
    if (!form.service_category_id) return [];
    const category = props.serviceCategories.find(c => c.id === form.service_category_id);
    return category?.services || [];
});

const onCategoryChange = () => {
    form.service_id = '';
};

const submit = () => {
    form.post(route('settings.sla-policies.store'), {
        onSuccess: () => {
            toast.success('SLA policy created successfully!');
        },
        onError: () => {
            toast.error('Failed to create SLA policy. Please check the form for errors.');
        }
    });
};
</script>
